import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import { AxiosRequestConfig } from 'axios';
import axiosInstance from 'config/axios';
import { CareArea } from 'interfaces/care-area';
import { Facility } from 'interfaces/facility';
import { FacilityCareArea } from 'interfaces/facility-care-area';
import { PaginationResponse } from 'interfaces/pagination-response';

/**
 * @description Facility care areas response type
 * @type {FacilityCareAreasResponse}
 */
export type FacilityCareAreasResponse = {
  records: CareArea[];
};

/**
 * @description Facility response type
 * @type {FacilityResponse}
 */
export type FacilityResponse = PaginationResponse<Facility>;

/**
 * @description Facility service
 * @type {FacilityService}
 */
const FacilityService = {
  /**
   * @description Create facility
   * @param {Partial<Facility>} data - Facility data
   * @returns {Promise<Facility>} Facility
   */
  create: (data: Partial<Facility>) => {
    return axiosInstance.post<Facility>('/v1/facilities', data);
  },

  /**
   * @description Update facility
   * @param {string | number} id - Facility id
   * @param {Partial<Facility>} data - Facility data
   * @returns {Promise<Facility>} Facility
   */
  update: (id: string | number, data: Partial<Facility>) => {
    return axiosInstance.patch<Facility>(`/v1/facilities/${id}`, data);
  },

  /**
   * @description Find facility
   * @param {string | number} id - Facility id
   * @param {AxiosRequestConfig} config - Axios request config
   * @returns {Promise<Facility>} Facility
   */
  find: (id: string | number, config?: AxiosRequestConfig) => {
    return axiosInstance.get<Facility>(`/v1/facilities/${id}`, config);
  },

  /**
   * @description Delete facility
   * @param {string | number} id - Facility id
   * @returns {Promise<Facility>} Facility
   */
  delete: (id: string | number) => {
    return axiosInstance.delete<Facility>(`/v1/facilities/${id}`);
  },

  /**
   * @description Get facilities
   * @param {Object} queryParams - Query parameters
   * @returns {Promise<FacilityResponse>} Facility response
   */
  get: (queryParams: {
    pageIndex: number;
    pageSize: number;
    sorting?: ColumnSort[];
    columnFilters?: ColumnFilter[];
  }) => {
    return axiosInstance.get<FacilityResponse>('/v1/facilities', {
      params: queryParams,
    });
  },

  /**
   * @description Get care areas
   * @param {string | number} facilityId - Facility id
   * @param {string | number} listDrugId - List drug id
   * @returns {Promise<FacilityCareAreasResponse>} Facility care areas response
   */
  getCareAreas: (facilityId: string | number, listDrugId?: string | number) => {
    let params = {};
    if (listDrugId) {
      params = { listDrugId };
    }

    return axiosInstance.get<FacilityCareAreasResponse>(
      `/v1/facilities/${facilityId}/care-areas`,
      {
        params,
      },
    );
  },

  /**
   * @description Add care area
   * @SADtrace SAD-003-4.1
   * @param {string | number} facilityId - Facility id
   * @param {string | number} careAreaId - Care area id
   * @returns {Promise<FacilityCareArea>} Facility care area
   */
  addCareArea: (facilityId: string | number, careAreaId: string | number) => {
    return axiosInstance.post<FacilityCareArea>(
      `/v1/facilities/${facilityId}/care-areas/${careAreaId}`,
    );
  },

  /**
   * @description Remove care area
   * @param {string | number} facilityId - Facility id
   * @param {string | number} careAreaId - Care area id
   * @returns {Promise<FacilityCareArea>} Facility care area
   */
  removeCareArea: (
    facilityId: string | number,
    careAreaId: string | number,
  ) => {
    return axiosInstance.delete<FacilityCareArea>(
      `/v1/facilities/${facilityId}/care-areas/${careAreaId}`,
    );
  },
};

export default FacilityService;
